import { useMemo, useState } from "react";
import useSWR from "swr";
import { getFetcher } from "../../../services/api";
import { Button } from "@nextui-org/react";
import IsValidIcon from "../../../components/elements/isValidIcon";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import ModalDetailDistribution from "../../../components/fragments/admin/report/distribution/ModalDetailDistribution";
import useListCompany from "../../useListCompany";
import { month } from "../../../enums/month";
import ReportStatusBadge from "../../../components/elements/ReportStatusBadge";
import useCustomNavigate from "../../useCustomNavigate";
import { useSelectedCompany } from "../../useSelectedCompany";
import useActivePeriodSelection from "../../useActivePeriodSelection";

export default function useDistribution() {
  const { editReportDistribution } = useCustomNavigate();
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedCompany, setSelectedCompany] = useSelectedCompany();
  const { dataCompany, isLoadingCompany } = useListCompany();

  /**
   * Periode default = periode aktif TERLAMA yang laporan distribusinya belum
   * selesai untuk perusahaan ini. Logika pemilihannya dipindah ke hook bersama
   * useActivePeriodSelection agar keempat modul pelaporan lain bisa memakainya
   * dengan resourcePath masing-masing.
   */
  const { selectedPeriod, setSelectedPeriod, dataPeriods, isLoadingPeriods } = useActivePeriodSelection(
    selectedCompany?.value,
    "pelaporan-bahan-b3-distribusi",
  );

  const { data, isLoading, mutate } = useSWR(
    selectedCompany?.value && selectedPeriod?.value ? `/api/pelaporan-bahan-b3-distribusi/search?returnAll=true&companyId=${selectedCompany.value}&periodId=${selectedPeriod.value}` : null,
    getFetcher
  );

  const columnsTableActivePeriod = useMemo(
    () => [
      { field: "id", headerName: "ID", valueGetter: (value, row) => row.bulan },
      { field: "perusahaan", headerName: "Perusahaan", valueGetter: (value, row) => row.company.name },
      { field: "jumlahB3Distribusi", headerName: "Jumlah Distribusi", valueGetter: (value, row) => `${row.jumlahB3Distribusi} KG` },
      { field: "periode", headerName: "Periode", valueGetter: (value, row) => row.period.name },
      { field: "bulan", headerName: "Bulan", valueGetter: (value, row) => month[row.bulan - 1] },
      {
        field: "status",
        headerName: "Status",
        renderCell: (params) => {
          const { status, jumlahB3Distribusi, laporanTersedia } = params.row;

          // Logika menentukan status Disimpan
          let computedStatus = status;
          if (!status || status === null) {
            if ((jumlahB3Distribusi && jumlahB3Distribusi !== 0 && laporanTersedia === true) || (jumlahB3Distribusi === 0 && laporanTersedia === false)) {
              computedStatus = "Disimpan";
            }
          }

          return <ReportStatusBadge status={computedStatus} />;
        },
        sortable: false,
        filterable: false,
      },
      {
        field: "isFinalized",
        headerName: "Finalisasi",
        renderCell: (params) => (
          <div className="mt-1">
            <IsValidIcon value={params.row.isFinalized} validMessage="Sudah Finalisasi" invalidMessage="Belum Finalisasi" />
          </div>
        ),
        sortable: false,
        filterable: false,
      },
      {
        field: "action",
        headerName: "Aksi",
        renderCell: (params) => (
          <div className="flex items-center gap-1">
            <ModalDetailDistribution id={params.row.id} />
            {!params.row.isFinalized && (
              <Button onPress={() => editReportDistribution(params.row.id)} size="sm" color="warning" isIconOnly>
                <PencilSquareIcon className="size-4" />
              </Button>
            )}
          </div>
        ),
        sortable: false,
        filterable: false,
      },
    ],
    [editReportDistribution]
  );

  return {
    dataFetch: {
      data,
      isLoading,
      mutate,
      dataCompany,
      isLoadingCompany,
      dataPeriods,
      isLoadingPeriods,
    },
    state: {
      selectedCompany,
      setSelectedCompany,
      selectedMaterial,
      setSelectedMaterial,
      selectedPeriod,
      setSelectedPeriod,
    },
    columnsTableActivePeriod,
  };
}